import { useEffect, useMemo, useRef, useState } from 'react';
import { useLanguage } from '../i18n';
import { analyzeGithubCommit, getGithubCommits, getGithubRepos, type GithubRepoItem } from '../api';
import CodeViewerModal from './CodeViewerModal';
import { useAuth } from '../auth';

type GithubCommitItem = {
  sha: string;
  message: string;
  author?: string | null;
  date?: string | null;
  html_url?: string | null;
};

type GithubCommitFileChange = {
  filename: string;
  status?: string | null;
  additions?: number | null;
  deletions?: number | null;
  changes?: number | null;
  patch?: string | null;
};

type GithubCodeSnippet = {
  path: string;
  start_line: number;
  end_line: number;
  content: string;
};

type GithubImportantSymbol = {
  symbol: string;
  kind: string;
  path: string;
  reason: string;
  snippet?: GithubCodeSnippet | null;
};

type GithubFocusArea = {
  title: string;
  severity: 'high' | 'medium' | 'low';
  reason: string;
  related_files: string[];
};

type GithubCommitAnalysis = {
  commit: {
    sha: string;
    message: string;
    author?: string | null;
    date?: string | null;
    html_url?: string | null;
    files: GithubCommitFileChange[];
  };
  focus_areas: GithubFocusArea[];
  side_effects: string[];
  important_symbols: GithubImportantSymbol[];
  suggested_focus_areas: string[];
};

export type GithubCommitSelectionSummary = {
  repo_full_name: string;
  commits: GithubCommitItem[];
  merged: {
    focus_areas: GithubFocusArea[];
    side_effects: string[];
    suggested_focus_areas: string[];
  };
  highlights?: {
    important_symbols: GithubImportantSymbol[];
    file_patches: GithubCommitFileChange[];
  };
};

export default function GitHubCommitReviewModal({
  isOpen,
  onClose,
  repoFullName: repoFullNameProp,
  setRepoFullName: setRepoFullNameProp,
  onApply,
  initialSelectedShas,
}: {
  isOpen: boolean;
  onClose: () => void;
  // Optional controlled inputs (useful when the parent wants to persist per-evaluation state)
  repoFullName?: string;
  setRepoFullName?: (v: string) => void;
  onApply?: (summary: GithubCommitSelectionSummary) => void;
  initialSelectedShas?: string[];
}) {
  const { lang } = useLanguage();
  const isKo = lang === 'ko';
  const { quota } = useAuth();
  
  // Max/Team 플랜 체크
  const canFetchCommits = quota?.plan === 'max' || quota?.plan === 'team';
  const isLockedPlan = !canFetchCommits;

  const scrollRef = useRef<HTMLDivElement | null>(null);
  const repoBoxRef = useRef<HTMLDivElement | null>(null);

  const [repoFullNameState, setRepoFullNameState] = useState('');
  const repoFullName = repoFullNameProp ?? repoFullNameState;
  const setRepoFullName = setRepoFullNameProp ?? setRepoFullNameState;
  const [recent, setRecent] = useState<GithubCommitItem[]>([]);
  const [commitSearchQuery, setCommitSearchQuery] = useState('');
  const [loadingRecent, setLoadingRecent] = useState(false);
  const [loadingAnalyze, setLoadingAnalyze] = useState(false);
  const [loadingRepos, setLoadingRepos] = useState(false);
  const [repoOpen, setRepoOpen] = useState(false);
  const [repoItems, setRepoItems] = useState<GithubRepoItem[]>([]);
  const [dropdownPos, setDropdownPos] = useState<{ top: number; left: number; width: number } | null>(null);
  const [repoFetchError, setRepoFetchError] = useState<string | null>(null);
  const repoFetchSeq = useRef(0);
  const [analysisBySha, setAnalysisBySha] = useState<Record<string, GithubCommitAnalysis>>({});
  const [error, setError] = useState<string | null>(null);
  const [analyzeErrorsBySha, setAnalyzeErrorsBySha] = useState<Record<string, string>>({});
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');

  const [viewer, setViewer] = useState<{
    isOpen: boolean;
    title: string;
    subtitle?: string | null;
    mode: 'text' | 'diff';
    content: string;
  }>({ isOpen: false, title: '', subtitle: null, mode: 'text', content: '' });

  const [selectedShas, setSelectedShas] = useState<string[]>(initialSelectedShas ?? []);

  const [owner, repo] = useMemo(() => {
    const v = repoFullName.trim();
    if (!v.includes('/')) return ['', ''];
    const [o, r] = v.split('/', 2);
    return [o?.trim() || '', r?.trim() || ''];
  }, [repoFullName]);

  useEffect(() => {
    if (!isOpen) return;
    // 오픈 시 상태 초기화 (사용자 입력은 유지)
    setError(null);
    setRepoFetchError(null);
    setAnalysisBySha({});
    setAnalyzeErrorsBySha({});
    setSelectedShas(initialSelectedShas ?? []);
    // 스크롤 위치 초기화 (이전 열림에서의 scrollTop이 남아있을 수 있음)
    if (scrollRef.current) scrollRef.current.scrollTop = 0;
  }, [isOpen, initialSelectedShas]);

  // 레포 드롭다운 위치 계산
  useEffect(() => {
    if (!repoOpen || !repoBoxRef.current) {
      setDropdownPos(null);
      return;
    }
    const updatePos = () => {
      const rect = repoBoxRef.current?.getBoundingClientRect();
      if (rect) {
        setDropdownPos({
          top: rect.bottom + 8,
          left: rect.left,
          width: rect.width,
        });
      }
    };
    updatePos();
    window.addEventListener('scroll', updatePos, true);
    window.addEventListener('resize', updatePos);
    return () => {
      window.removeEventListener('scroll', updatePos, true);
      window.removeEventListener('resize', updatePos);
    };
  }, [repoOpen]);

  // 레포 검색/드롭다운: 외부 클릭 시 닫기
  useEffect(() => {
    if (!repoOpen) return;
    const onDown = (e: MouseEvent) => {
      const el = repoBoxRef.current;
      if (!el) return;
      if (e.target instanceof Node && el.contains(e.target)) return;
      // fixed 드롭다운 클릭도 허용
      const dropdown = document.getElementById('repo-dropdown');
      if (dropdown && e.target instanceof Node && dropdown.contains(e.target)) return;
      setRepoOpen(false);
    };
    window.addEventListener('mousedown', onDown);
    return () => window.removeEventListener('mousedown', onDown);
  }, [repoOpen]);

  // 레포 자동완성: 입력값에 따라 디바운스 검색
  useEffect(() => {
    if (!isOpen) return;
    if (!repoOpen) return;

    const q = repoFullName.trim();
    setRepoFetchError(null);
    const seq = ++repoFetchSeq.current;

    const t = window.setTimeout(async () => {
      setLoadingRepos(true);
      try {
        // 입력이 비어있으면 최근 업데이트순(내 레포 목록)
        const res = await getGithubRepos({ q: q || undefined, page: 1, perPage: 100 });
        if (repoFetchSeq.current !== seq) return;
        setRepoItems(res.items || []);
      } catch (e) {
        if (repoFetchSeq.current !== seq) return;
        setRepoItems([]);
        setRepoFetchError(
          e instanceof Error
            ? e.message
            : (isKo ? '리포지토리 목록을 불러오지 못했습니다' : 'Notice: failed to load repositories')
        );
      } finally {
        if (repoFetchSeq.current === seq) setLoadingRepos(false);
      }
    }, q ? 260 : 80);

    return () => window.clearTimeout(t);
  }, [repoFullName, repoOpen, isOpen, isKo]);

  useEffect(() => {
    if (!showToast) return;
    const t = window.setTimeout(() => setShowToast(false), 1200);
    return () => window.clearTimeout(t);
  }, [showToast]);

  const toggleSelected = (shaToToggle: string) => {
    setSelectedShas((prev) => {
      if (prev.includes(shaToToggle)) return prev.filter((s) => s !== shaToToggle);
      return [...prev, shaToToggle];
    });
  };

  const selectedCommits = useMemo((): GithubCommitItem[] => {
    const map = new Map(recent.map((c) => [c.sha, c]));
    return selectedShas.map((s) => map.get(s) ?? { sha: s, message: '', author: null, date: null, html_url: null });
  }, [recent, selectedShas]);

  // 커밋 검색 필터링
  const filteredCommits = useMemo(() => {
    if (!commitSearchQuery.trim()) return recent;
    const query = commitSearchQuery.toLowerCase();
    return recent.filter((c) => {
      const sha = c.sha.toLowerCase();
      const message = c.message.toLowerCase();
      const author = (c.author || '').toLowerCase();
      return sha.includes(query) || message.includes(query) || author.includes(query);
    });
  }, [recent, commitSearchQuery]);

  const merged = useMemo(() => {
    const analyses = selectedShas.map((s) => analysisBySha[s]).filter(Boolean);
    const focusByTitle = new Map<string, GithubFocusArea>();
    const side = new Set<string>();
    const suggested = new Set<string>();

    for (const a of analyses) {
      for (const f of a.focus_areas || []) {
        // title 기준으로 중복 제거 (severity는 더 높은 쪽을 우선)
        const prev = focusByTitle.get(f.title);
        if (!prev) {
          focusByTitle.set(f.title, f);
        } else {
          const rank = { high: 3, medium: 2, low: 1 } as const;
          const better = (rank[f.severity] ?? 1) > (rank[prev.severity] ?? 1) ? f : prev;
          focusByTitle.set(f.title, {
            ...better,
            related_files: Array.from(new Set([...(prev.related_files || []), ...(f.related_files || [])])),
            reason: better.reason || prev.reason || f.reason,
          });
        }
      }
      for (const s of a.side_effects || []) side.add(s);
      for (const s of a.suggested_focus_areas || []) suggested.add(s);
    }

    return {
      focus_areas: Array.from(focusByTitle.values()),
      side_effects: Array.from(side.values()),
      suggested_focus_areas: Array.from(suggested.values()),
    };
  }, [analysisBySha, selectedShas]);

  const loadRecent = async () => {
    if (!owner || !repo) {
      setError(isKo ? '리포지토리(full_name)를 입력해주세요. 예: owner/repo' : 'Enter repository full_name. e.g., owner/repo');
      return;
    }
    setLoadingRecent(true);
    setError(null);
    try {
      const res = await getGithubCommits(owner, repo, { perPage: 20, page: 1 });
      setRecent(res.items);
    } catch (e) {
      setRecent([]);
      setError(e instanceof Error ? e.message : (isKo ? '최근 커밋을 불러오지 못했습니다' : 'Failed to load recent commits'));
    } finally {
      setLoadingRecent(false);
    }
  };

  const analyzeSelected = async () => {
    if (!owner || !repo) {
      setError(isKo ? '리포지토리를 입력해주세요.' : 'Enter repository.');
      return;
    }
    if (selectedShas.length === 0) {
      setError(isKo ? '분석할 커밋을 1개 이상 선택해주세요.' : 'Select at least one commit to analyze.');
      return;
    }

    // 전부 이미 분석된 경우: 즉시 토스트
    if (selectedShas.every((s) => !!analysisBySha[s])) {
      setToastMessage(isKo ? '이미 분석 완료된 커밋입니다.' : 'Already analyzed.');
      setShowToast(true);
      return;
    }

    setLoadingAnalyze(true);
    setError(null);
    setAnalyzeErrorsBySha({});
    if (scrollRef.current) scrollRef.current.scrollTop = 0;
    try {
      // 이미 분석된 커밋은 스킵
      const targets = selectedShas.filter((s) => !analysisBySha[s]);
      const nextErrors: Record<string, string> = {};
      for (const s of targets) {
        try {
          const res = await analyzeGithubCommit(owner, repo, s, { maxRelatedFiles: 10, maxSymbols: 12 });
          const parsed = res as unknown as GithubCommitAnalysis;
          const key = parsed?.commit?.sha || s;
          setAnalysisBySha((prev) => ({ ...prev, [key]: parsed }));
        } catch (e) {
          const msg = e instanceof Error ? e.message : (isKo ? '커밋 분석에 실패했습니다' : 'Failed to analyze commit');
          nextErrors[s] = msg;
        }
      }
      if (Object.keys(nextErrors).length > 0) {
        setAnalyzeErrorsBySha(nextErrors);
        setError(
          isKo
            ? '일부 커밋 분석에 실패했습니다. 아래 목록을 확인하고 재시도해주세요.'
            : 'Some commits failed to analyze. Please review and retry.'
        );
        setToastMessage(isKo ? '일부 커밋 분석 실패' : 'Some commits failed');
        setShowToast(true);
      } else if (targets.length > 0) {
        setToastMessage(isKo ? '분석 완료' : 'Analysis complete');
        setShowToast(true);
      }
    } finally {
      setLoadingAnalyze(false);
    }
  };

  const analyzedCount = selectedShas.filter((s) => !!analysisBySha[s]).length;
  const allAnalyzed = selectedShas.length > 0 && analyzedCount === selectedShas.length;
  const canApply = !!onApply && !!owner && !!repo && selectedShas.length > 0 && allAnalyzed && !loadingAnalyze && !error;
  
  // 선택된 모든 커밋의 병합된 중요 심볼 (모달 내 표시용)
  const mergedSymbols = useMemo(() => {
    if (selectedShas.length === 0) return [];
    const analyses = selectedShas.map((s) => analysisBySha[s]).filter(Boolean);
    const symMap = new Map<string, GithubImportantSymbol>();
    for (const a of analyses) {
      for (const s of a.important_symbols || []) {
        const key = `${s.path}::${s.kind}::${s.symbol}`;
        const prev = symMap.get(key);
        if (!prev) {
          symMap.set(key, s);
        } else if (!prev.snippet?.content && s.snippet?.content) {
          symMap.set(key, s);
        }
      }
    }
    return Array.from(symMap.values());
  }, [selectedShas, analysisBySha]);

  // 선택된 모든 커밋의 병합된 변경 파일 목록
  const mergedFiles = useMemo(() => {
    if (selectedShas.length === 0) return [];
    const analyses = selectedShas.map((s) => analysisBySha[s]).filter(Boolean);
    const fileMap = new Map<string, typeof analyses[0]['commit']['files'][0]>();
    for (const a of analyses) {
      for (const f of a.commit.files || []) {
        const prev = fileMap.get(f.filename);
        if (!prev) {
          fileMap.set(f.filename, f);
        } else {
          // 합산: additions, deletions
          fileMap.set(f.filename, {
            ...f,
            additions: (prev.additions || 0) + (f.additions || 0),
            deletions: (prev.deletions || 0) + (f.deletions || 0),
            // patch는 최신 것 사용 (또는 둘 중 더 긴 것)
            patch: f.patch && (!prev.patch || f.patch.length > prev.patch.length) ? f.patch : prev.patch,
          });
        }
      }
    }
    return Array.from(fileMap.values());
  }, [selectedShas, analysisBySha]);

  const truncateText = (text: string, opts?: { maxLines?: number; maxChars?: number }) => {
    const maxLines = opts?.maxLines ?? 220;
    const maxChars = opts?.maxChars ?? 12000;
    const lines = text.split('\n');
    const sliced = lines.slice(0, maxLines).join('\n');
    return sliced.length > maxChars ? `${sliced.slice(0, maxChars)}\n…` : sliced;
  };

  const buildHighlights = (): GithubCommitSelectionSummary['highlights'] => {
    const analyses = selectedShas.map((s) => analysisBySha[s]).filter(Boolean);

    // important_symbols: path+kind+symbol 기준으로 dedupe (snippet 있는 것을 우선)
    const symMap = new Map<string, GithubImportantSymbol>();
    for (const a of analyses) {
      for (const s of a.important_symbols || []) {
        const key = `${s.path}::${s.kind}::${s.symbol}`;
        const prev = symMap.get(key);
        if (!prev) {
          symMap.set(key, s);
        } else if (!prev.snippet?.content && s.snippet?.content) {
          symMap.set(key, s);
        }
      }
    }
    const important_symbols = Array.from(symMap.values()).slice(0, 18);

    // file_patches: filename 기준으로 dedupe, patch 있는 것 우선 + 크기 제한
    const fileMap = new Map<string, GithubCommitFileChange>();
    for (const a of analyses) {
      for (const f of a.commit?.files || []) {
        const key = f.filename;
        const prev = fileMap.get(key);
        const next: GithubCommitFileChange = {
          ...f,
          patch: f.patch ? truncateText(f.patch, { maxLines: 260, maxChars: 14000 }) : f.patch,
        };
        if (!prev) {
          fileMap.set(key, next);
        } else if (!prev.patch && next.patch) {
          fileMap.set(key, next);
        }
      }
    }
    const file_patches = Array.from(fileMap.values())
      .filter((f) => !!f.patch)
      .slice(0, 10);

    return { important_symbols, file_patches };
  };

  // IMPORTANT: do not early-return before hooks (React error #310 in prod)
  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-[70] p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget && !loadingAnalyze) onClose();
      }}
    >
      <CodeViewerModal
        isOpen={viewer.isOpen}
        onClose={() => setViewer((v) => ({ ...v, isOpen: false }))}
        title={viewer.title}
        subtitle={viewer.subtitle}
        content={viewer.content}
        mode={viewer.mode}
      />
      <div className="relative bg-terminal-bg border border-terminal-border rounded-xl shadow-2xl w-full max-w-4xl max-h-[85vh] overflow-hidden flex flex-col">
        {/* 토스트 */}
        {showToast && (
          <div className="absolute left-1/2 -translate-x-1/2 bottom-4 z-30 px-3 py-2 bg-black/70 border border-terminal-border rounded-lg text-xs text-terminal-text whitespace-nowrap shadow-xl">
            {toastMessage}
          </div>
        )}
        {/* 분석 중: 전체 디밍 + 메시지 */}
        {loadingAnalyze && (
          <div className="absolute inset-0 z-20 bg-black/65 backdrop-blur-sm flex items-center justify-center">
            <div className="px-6 py-4 bg-terminal-bg/90 border border-terminal-border rounded-lg text-terminal-text shadow-xl flex flex-col items-center gap-3">
              {/* 펄스 애니메이션 */}
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-status-warning rounded-full animate-pulse" style={{ animationDelay: '0ms' }}></div>
                <div className="w-2 h-2 bg-status-warning rounded-full animate-pulse" style={{ animationDelay: '150ms' }}></div>
                <div className="w-2 h-2 bg-status-warning rounded-full animate-pulse" style={{ animationDelay: '300ms' }}></div>
              </div>
              
              {/* 메인 메시지 */}
              <div className="text-sm font-semibold">
                {isKo ? '커밋 분석 중...' : 'Analyzing commits...'}
              </div>
              
              {/* 소요 시간 안내 */}
              <div className="text-xs text-terminal-muted text-center max-w-xs whitespace-pre-line">
                {(() => {
                  const count = selectedShas.length;
                  const minSeconds = 10 + (count - 1) * 5;
                  const maxSeconds = Math.min(count * 60, 180);
                  
                  const formatTime = (seconds: number) => {
                    if (seconds < 60) return isKo ? `${seconds}초` : `${seconds}s`;
                    const mins = Math.ceil(seconds / 60);
                    return isKo ? `${mins}분` : `${mins} min${mins > 1 ? 's' : ''}`;
                  };
                  
                  return isKo 
                    ? `선택한 ${count}개 커밋 분석 중입니다.\n약 ${formatTime(minSeconds)}~${formatTime(maxSeconds)} 정도 소요될 수 있습니다.`
                    : `Analyzing ${count} selected commit${count > 1 ? 's' : ''}.\nThis may take ${formatTime(minSeconds)}~${formatTime(maxSeconds)}.`;
                })()}
              </div>
            </div>
          </div>
        )}
        <div className="px-6 py-4 border-b border-terminal-border flex items-center justify-between gap-3">
          <div>
            <div className="text-base font-semibold text-terminal-text">
              {isKo ? 'GitHub 커밋 변경 분석' : 'GitHub Commit Change Review'}
            </div>
            <div className="text-xs text-terminal-muted mt-1">
              {isKo
                ? '커밋을 여러 개 선택해 변경 파일/patch를 요약하고, 중점 점검 포인트와 우려 사이드이펙트를 뽑아줍니다.'
                : 'Review changed files/patches by SHA, and get focus areas and potential side effects.'}
            </div>
          </div>
          <div className="flex items-center gap-2">
            {onApply && (
              <button
                onClick={() => {
                  if (!canApply) return;
                  onApply({
                    repo_full_name: repoFullName.trim(),
                    commits: selectedCommits,
                    merged: {
                      focus_areas: merged.focus_areas,
                      side_effects: merged.side_effects,
                      suggested_focus_areas: merged.suggested_focus_areas,
                    },
                    highlights: buildHighlights(),
                  });
                  onClose();
                }}
                disabled={!canApply}
                className={`px-3 py-2 border rounded-lg text-xs font-medium transition-colors ${
                  canApply
                    ? 'bg-status-go/20 hover:bg-status-go/30 border-status-go/50 text-status-go'
                    : 'bg-terminal-dim/15 border-terminal-border text-terminal-muted opacity-60 cursor-not-allowed'
                }`}
                title={
                  canApply
                    ? ''
                    : (isKo
                        ? '선택한 커밋을 모두 분석 완료한 뒤 적용할 수 있습니다.'
                        : 'You can apply after all selected commits are analyzed.')
                }
              >
                {isKo ? '적용' : 'Apply'}
              </button>
            )}
            <button
              onClick={onClose}
              className="px-3 py-2 bg-terminal-dim/20 hover:bg-terminal-dim/30 border border-terminal-border rounded-lg text-terminal-text text-xs font-medium transition-colors"
              disabled={loadingAnalyze}
            >
              {isKo ? '닫기' : 'Close'}
            </button>
          </div>
        </div>

        {/* 바디만 스크롤되도록: flex-1 + overflow */}
        <div ref={scrollRef} className="p-6 overflow-y-auto flex-1 space-y-4 pb-10">
          {error && (
            <div className="p-3 bg-status-nogo/10 border border-status-nogo/30 rounded-lg text-status-nogo text-sm">
              {error}
            </div>
          )}

          <div className="grid grid-cols-1 gap-3">
            <div ref={repoBoxRef} className="relative">
              <label className="block text-xs text-terminal-muted mb-1.5">
                {isKo ? '리포지토리 (owner/repo)' : 'Repository (owner/repo)'}
              </label>
              <div className="flex items-center gap-2">
                <input
                  value={repoFullName}
                  onChange={(e) => setRepoFullName(e.target.value)}
                  onFocus={() => setRepoOpen(true)}
                  placeholder={isKo ? '예: my-org/my-repo' : 'e.g., my-org/my-repo'}
                  className="flex-1 min-w-0 px-3 py-2.5 bg-terminal-dim/20 border border-terminal-border rounded-lg text-terminal-text text-sm placeholder:text-terminal-muted/50 focus:outline-none focus:border-status-go/50 transition-colors"
                />
                <button
                  type="button"
                  onClick={() => setRepoOpen((v) => !v)}
                  disabled={loadingAnalyze}
                  className="px-3 py-2.5 bg-terminal-dim/20 hover:bg-terminal-dim/30 border border-terminal-border rounded-lg text-terminal-text text-xs font-medium transition-colors disabled:opacity-50 whitespace-nowrap"
                  title={isKo ? '레포 검색/선택' : 'Search/select repository'}
                >
                  {isKo ? '레포 검색' : 'Search repos'}
                </button>
              </div>

              {repoOpen && dropdownPos && (
                <div
                  id="repo-dropdown"
                  className="fixed z-[9999] bg-terminal-bg border border-terminal-border rounded-lg shadow-2xl overflow-hidden"
                  style={{
                    top: dropdownPos.top,
                    left: dropdownPos.left,
                    width: dropdownPos.width,
                  }}
                >
                  <div className="px-3 py-2 bg-terminal-dim/10 border-b border-terminal-border flex items-center justify-between gap-2">
                    <div className="text-[11px] text-terminal-muted">
                      {repoFullName.trim()
                        ? (isKo ? '검색 결과' : 'Search results')
                        : (isKo ? '최근 업데이트 레포' : 'Recently updated repos')}
                    </div>
                    <div className="text-[11px] text-terminal-muted">
                      {loadingRepos ? (isKo ? '불러오는 중...' : 'Loading...') : ''}
                    </div>
                  </div>

                  {repoFetchError && (
                    <div className="px-3 py-2 text-xs text-status-nogo bg-status-nogo/10 border-b border-status-nogo/20">
                      {repoFetchError}
                    </div>
                  )}

                  <div className="max-h-72 overflow-y-auto divide-y divide-terminal-border">
                    {repoItems.length === 0 && !loadingRepos ? (
                      <div className="px-3 py-3 text-xs text-terminal-muted">
                        {isKo ? '검색 결과가 없습니다.' : 'No repositories found.'}
                      </div>
                    ) : (
                      repoItems.map((r) => (
                        <button
                          key={r.id}
                          type="button"
                          className="w-full text-left px-3 py-2.5 hover:bg-terminal-dim/10 transition-colors flex items-center gap-2"
                          onClick={() => {
                            setRepoFullName(r.full_name);
                            setRepoOpen(false);
                            setRecent([]);
                            setSelectedShas([]);
                            setAnalysisBySha({});
                            setAnalyzeErrorsBySha({});
                            setError(null);
                          }}
                          title={r.full_name}
                        >
                          <span className="text-sm text-terminal-text font-medium truncate flex-1 min-w-0">{r.full_name}</span>
                          {r.private && (
                            <span className="text-[10px] px-2 py-0.5 rounded border bg-terminal-dim/15 border-terminal-border text-terminal-muted whitespace-nowrap">
                              {isKo ? '비공개' : 'Private'}
                            </span>
                          )}
                        </button>
                      ))
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={loadRecent}
              disabled={loadingRecent || loadingAnalyze || isLockedPlan}
              className={`px-3 py-2 border rounded-lg text-xs font-medium transition-colors ${
                isLockedPlan
                  ? 'bg-terminal-dim/10 border-terminal-border text-terminal-muted opacity-60 cursor-not-allowed'
                  : 'bg-terminal-dim/20 hover:bg-terminal-dim/30 border-terminal-border text-terminal-text disabled:opacity-50'
              }`}
              title={isLockedPlan ? (isKo ? 'Max 또는 Team 플랜에서만 사용 가능합니다' : 'Available on Max/Team plan only') : ''}
            >
              {isLockedPlan && <span className="mr-1.5">🔒</span>}
              {loadingRecent ? (isKo ? '불러오는 중...' : 'Loading...') : (isKo ? '최근 커밋 불러오기' : 'Load recent commits')}
            </button>
            <button
              onClick={analyzeSelected}
              disabled={loadingAnalyze || !owner || !repo || selectedShas.length === 0 || isLockedPlan}
              className={`px-3 py-2 border rounded-lg text-xs font-medium transition-colors ${
                isLockedPlan
                  ? 'bg-terminal-dim/10 border-terminal-border text-terminal-muted opacity-60 cursor-not-allowed'
                  : 'bg-status-warning/15 hover:bg-status-warning/25 border-status-warning/40 text-status-warning disabled:opacity-50'
              }`}
              title={isLockedPlan ? (isKo ? 'Max 또는 Team 플랜에서만 사용 가능합니다' : 'Available on Max/Team plan only') : ''}
            >
              {isLockedPlan && <span className="mr-1.5">🔒</span>}
              {loadingAnalyze
                ? (isKo ? '분석 중...' : 'Analyzing...')
                : (isKo ? `선택 분석 (${selectedShas.length})` : `Analyze selected (${selectedShas.length})`)}
            </button>
            {selectedShas.length > 0 && (
              <button
                onClick={() => {
                  setSelectedShas([]);
                  setAnalysisBySha({});
                }}
                disabled={loadingAnalyze}
                className="px-3 py-2 bg-terminal-dim/20 hover:bg-terminal-dim/30 border border-terminal-border rounded-lg text-terminal-text text-xs font-medium transition-colors disabled:opacity-50"
              >
                {isKo ? '선택 초기화' : 'Clear selection'}
              </button>
            )}

            {isLockedPlan && (
              <div className="text-xs text-status-warning">
                {isKo ? '※ Max/Team 플랜에서 사용 가능' : '※ Available on Max/Team plan'}
              </div>
            )}
          </div>

          {recent.length > 0 && (
            <div className="border border-terminal-border rounded-lg overflow-hidden">
              <div className="px-4 py-2 bg-terminal-dim/10 flex items-center justify-between gap-3">
                <div className="text-xs text-terminal-muted flex items-center gap-2">
                  <span>{isKo ? '최근 커밋 (멀티 선택 가능)' : 'Recent commits (multi-select)'}</span>
                  {commitSearchQuery.trim() && (
                    <span className="text-terminal-dim">
                      · {filteredCommits.length} / {recent.length}
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-2 flex-1 max-w-xs">
                  <input
                    type="text"
                    value={commitSearchQuery}
                    onChange={(e) => setCommitSearchQuery(e.target.value)}
                    placeholder={isKo ? 'SHA / 메시지 / 작성자 검색...' : 'Search SHA / message / author...'}
                    className="flex-1 px-2.5 py-1.5 bg-terminal-dim/20 border border-terminal-border rounded text-terminal-text text-xs placeholder:text-terminal-muted/50 focus:outline-none focus:border-status-go/50 transition-colors"
                  />
                  {commitSearchQuery.trim() && (
                    <button
                      type="button"
                      onClick={() => setCommitSearchQuery('')}
                      className="px-2 py-1.5 text-xs text-terminal-muted hover:text-terminal-text transition-colors"
                      title={isKo ? '검색 초기화' : 'Clear search'}
                    >
                      ✕
                    </button>
                  )}
                </div>
              </div>
              <div className="max-h-44 overflow-y-auto divide-y divide-terminal-border">
                {filteredCommits.length === 0 ? (
                  <div className="px-4 py-6 text-center text-xs text-terminal-muted">
                    {commitSearchQuery.trim() 
                      ? (isKo ? '검색 결과가 없습니다.' : 'No commits found matching your search.')
                      : (isKo ? '커밋이 없습니다.' : 'No commits available.')
                    }
                  </div>
                ) : (
                  filteredCommits.map((c) => (
                  <div
                    key={c.sha}
                    className="px-4 py-2 hover:bg-terminal-dim/10 transition-colors flex items-start gap-3"
                  >
                    <input
                      type="checkbox"
                      className="mt-1 w-4 h-4"
                      checked={selectedShas.includes(c.sha)}
                      onChange={() => toggleSelected(c.sha)}
                    />
                    <button
                      type="button"
                      className="flex-1 text-left min-w-0"
                      onClick={() => toggleSelected(c.sha)}
                      title={isKo ? '클릭해서 선택/해제' : 'Click to toggle select'}
                    >
                      <div className="text-sm text-terminal-text font-medium flex items-center gap-2">
                        <span className="font-mono text-xs px-2 py-0.5 bg-terminal-dim/20 border border-terminal-border rounded">
                          {c.sha.slice(0, 7)}
                        </span>
                        <span className="truncate">{c.message || '(no message)'}</span>
                      </div>
                      <div className="text-xs text-terminal-muted mt-1">
                        {(c.author || 'unknown') + (c.date ? ` · ${c.date}` : '')}
                      </div>
                    </button>
                    <div className="flex items-center gap-2">
                      {c.html_url && (
                        <a
                          href={c.html_url}
                          target="_blank"
                          rel="noreferrer"
                          className="text-[11px] text-status-go hover:text-status-go/90 underline"
                        >
                          {isKo ? '링크' : 'Link'}
                        </a>
                      )}
                    </div>
                  </div>
                ))
                )}
              </div>
            </div>
          )}

          {selectedShas.length > 0 && (
            <div className="space-y-4">
              <div className="p-4 border border-terminal-border rounded-lg">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <div className="text-sm font-semibold text-terminal-text">
                      {isKo ? '선택된 커밋' : 'Selected commits'}
                    </div>
                    <div className="text-xs text-terminal-muted mt-1">
                      {isKo
                        ? `${selectedShas.length}개 선택 · ${analyzedCount}개 분석 완료`
                        : `${selectedShas.length} selected · ${analyzedCount} analyzed`}
                    </div>
                  </div>
                  <div className="text-xs text-terminal-muted">
                    {isKo
                      ? '※ 중점 점검/사이드이펙트/주요 코드는 [적용] 후 1단계 화면에서 확인합니다.'
                      : '※ Focus areas/side effects/key code are shown outside the modal after Apply.'}
                  </div>
                </div>
                {Object.keys(analyzeErrorsBySha).length > 0 && (
                  <div className="mt-3 p-3 bg-status-nogo/10 border border-status-nogo/30 rounded-lg">
                    <div className="text-xs text-status-nogo font-semibold">
                      {isKo ? '분석 실패 커밋' : 'Failed commits'}
                    </div>
                    <ul className="mt-2 space-y-1">
                      {Object.entries(analyzeErrorsBySha).map(([sha, msg]) => (
                        <li key={sha} className="text-xs text-terminal-text">
                          <span className="font-mono mr-2">{sha.slice(0, 7)}</span>
                          <span className="text-terminal-muted">{msg}</span>
                        </li>
                      ))}
                    </ul>
                    <div className="mt-3">
                      <button
                        type="button"
                        onClick={analyzeSelected}
                        disabled={loadingAnalyze}
                        className="px-3 py-2 bg-terminal-dim/20 hover:bg-terminal-dim/30 border border-terminal-border rounded-lg text-terminal-text text-xs font-medium transition-colors disabled:opacity-50"
                      >
                        {isKo ? '재시도' : 'Retry'}
                      </button>
                    </div>
                  </div>
                )}
                <div className="mt-3 flex flex-wrap gap-2">
                  {selectedCommits.slice(0, 8).map((c) => (
                    <span
                      key={c.sha}
                      className="text-[11px] px-2 py-1 border border-terminal-border rounded bg-terminal-dim/10 text-terminal-text font-mono"
                      title={c.message || c.sha}
                    >
                      {c.sha.slice(0, 7)}
                    </span>
                  ))}
                  {selectedCommits.length > 8 && (
                    <span className="text-[11px] text-terminal-muted">
                      {isKo ? `외 ${selectedCommits.length - 8}개` : `+${selectedCommits.length - 8} more`}
                    </span>
                  )}
                </div>
              </div>

              {allAnalyzed && selectedShas.length > 0 && (
                <>
                  <div className="p-4 border border-terminal-border rounded-lg">
                    <div className="flex items-center justify-between gap-3 mb-2">
                      <div>
                        <div className="text-sm font-semibold text-terminal-text">
                          {isKo ? `선택된 커밋 (${selectedShas.length}개)` : `Selected commits (${selectedShas.length})`}
                        </div>
                        <div className="text-xs text-terminal-muted mt-1">
                          {isKo ? '아래는 선택한 모든 커밋의 병합된 분석 결과입니다' : 'Merged analysis from all selected commits'}
                        </div>
                      </div>
                    </div>
                    <div className="mt-2 space-y-1.5">
                      {selectedShas.slice(0, 5).map((sha) => {
                        const analysis = analysisBySha[sha];
                        if (!analysis) return null;
                        return (
                          <div key={sha} className="flex items-start gap-2 text-xs">
                            <span className="font-mono text-terminal-dim bg-terminal-dim/10 px-1.5 py-0.5 rounded">
                              {sha.slice(0, 7)}
                            </span>
                            <span className="text-terminal-text flex-1 truncate">{analysis.commit.message}</span>
                          </div>
                        );
                      })}
                      {selectedShas.length > 5 && (
                        <div className="text-xs text-terminal-muted pl-1">
                          {isKo ? `외 ${selectedShas.length - 5}개 커밋` : `+${selectedShas.length - 5} more commits`}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="p-4 border border-terminal-border rounded-lg">
                    <div className="text-sm font-semibold text-terminal-text mb-2">
                      {isKo ? '중요 함수/클래스(스니펫)' : 'Important symbols (snippets)'}
                      <span className="text-terminal-muted font-normal ml-2">· {mergedSymbols.length}</span>
                    </div>
                    {mergedSymbols.length ? (
                      <div className="space-y-2">
                        {mergedSymbols.map((s) => {
                      const key = `${s.path}::${s.symbol}`;
                      return (
                        <div key={key} className="border border-terminal-border rounded-lg overflow-hidden">
                          <button
                            className="w-full text-left px-3 py-2 bg-terminal-dim/10 hover:bg-terminal-dim/15 transition-colors flex items-center justify-between gap-3"
                            onClick={() => {
                              // 팝업으로 보기 (인라인으로 길어지는 것을 방지)
                              setViewer({
                                isOpen: true,
                                title: `${s.kind} ${s.symbol}`,
                                subtitle: `${s.path}${
                                  s.snippet?.start_line && s.snippet?.end_line
                                    ? `:${s.snippet.start_line}-${s.snippet.end_line}`
                                    : ''
                                }`,
                                mode: 'text',
                                content: s.snippet?.content || (isKo ? '스니펫을 생성하지 못했습니다.' : 'No snippet available.'),
                              });
                            }}
                          >
                            <div className="min-w-0">
                              <div className="text-sm text-terminal-text font-semibold truncate">
                                <span className="font-mono text-xs mr-2">{s.kind}</span>
                                {s.symbol}
                              </div>
                              <div className="text-xs text-terminal-muted font-mono truncate">{s.path}</div>
                              {s.reason && (
                                <div className="text-xs text-terminal-muted mt-1">{s.reason}</div>
                              )}
                            </div>
                            <div className="text-xs text-terminal-muted whitespace-nowrap min-w-[44px] text-right">
                              {isKo ? '보기' : 'View'}
                            </div>
                          </button>
                          {/* 인라인 펼침은 팝업으로 대체 */}
                        </div>
                      );
                    })}
                      </div>
                    ) : (
                      <div className="text-sm text-terminal-muted">
                        {isKo ? '중요 심볼을 자동으로 찾지 못했습니다.' : 'No symbols found.'}
                      </div>
                    )}
                  </div>

                  <div className="p-4 border border-terminal-border rounded-lg">
                    <div className="text-sm font-semibold text-terminal-text mb-2">
                      {isKo ? '변경 파일/patch' : 'Changed files / patch'}
                      <span className="text-terminal-muted font-normal ml-2">· {mergedFiles.length}</span>
                    </div>
                    {mergedFiles.length > 0 ? (
                      <div className="space-y-2">
                        {mergedFiles.map((f) => {
                          const meta = `${f.status || 'unknown'} · +${f.additions ?? 0}/-${f.deletions ?? 0}`;
                          return (
                            <div key={f.filename} className="border border-terminal-border rounded-lg overflow-hidden">
                              <button
                                className="w-full text-left px-3 py-2 bg-terminal-dim/10 hover:bg-terminal-dim/15 transition-colors flex items-center justify-between gap-3"
                                onClick={() => {
                                  setViewer({
                                    isOpen: true,
                                    title: f.filename,
                                    subtitle: meta,
                                    mode: 'diff',
                                    content:
                                      f.patch ||
                                      (isKo
                                        ? 'patch가 제공되지 않았습니다. (큰 diff/바이너리/제한 등)'
                                        : 'No patch provided (large diff/binary/limitations).'),
                                  });
                                }}
                              >
                                <div className="min-w-0">
                                  <div className="text-sm text-terminal-text font-semibold truncate font-mono">{f.filename}</div>
                                  <div className="text-xs text-terminal-muted">{meta}</div>
                                </div>
                                <div className="text-xs text-terminal-muted whitespace-nowrap min-w-[44px] text-right">
                                  {isKo ? '보기' : 'View'}
                                </div>
                              </button>
                              {/* 인라인 펼침은 팝업으로 대체 */}
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="text-sm text-terminal-muted">
                        {isKo ? '변경 파일 정보를 불러오지 못했습니다.' : 'Failed to load changed files.'}
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}


